import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  Search,
  Trash2,
  ChevronDown,
  Eye,
  FileText,
  Pencil,
  Upload,
  Download,
} from 'lucide-react';
import { ChatBlockedState } from '../../../chat/components/molecules/ChatBlockedState';
import {
  TaskpanePanelShell,
  PanelSectionHeader,
  PanelSearchField,
  PanelEmptyState,
} from '@/taskpane/shared/components/panel-layout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  addTemplateToExecution,
  addUploadedFileTemplateToExecution,
  deleteExecutionTemplate,
  downloadExecutionTemplateRow,
  getExecutionTemplates,
  listTemplatesCatalog,
  updateExecutionTemplateFields,
  type CatalogTemplate,
  type ExecutionTemplateRow,
  type ExecutionTemplateStatus,
  BLANK_TEMPLATE_ID,
  BLANK_EXCEL_TEMPLATE_ID,
} from '../../services/executionTemplatesService';
import { emitExecutionTemplatesChanged } from '../../services/executionTemplatesEvents';
import { configService } from '../../../../shared/services/configService';
import ExecutionTemplatePreviewDialog from './ExecutionTemplatePreviewDialog';

const WORD_ICON_SRC = 'assets/icons/microsoft-word-icon.svg';
const EXCEL_ICON_SRC = 'assets/icons/microsoft-excel-logo.svg';
const PDF_ICON_SRC = 'assets/icons/pdf-icon.svg';

const STATUS_OPTIONS: { value: ExecutionTemplateStatus; label: string }[] = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'completed', label: 'Completado' },
  { value: 'canceled', label: 'Cancelado' },
];

interface ExecutionTemplatesPanelProps {
  executionId?: string | null;
  onOpenProjectSelector?: () => void;
}

function CatalogTypeIcon({ type }: { type: string | null }) {
  const t = (type || '').toLowerCase();
  if (t === 'word' || t === 'scratch') {
    return <img src={WORD_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />;
  }
  if (t === 'excel' || t === 'xlsx' || t === 'xls' || t === 'csv') {
    return <img src={EXCEL_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />;
  }
  if (t === 'pdf') {
    return <img src={PDF_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />;
  }
  return <FileText className="h-4 w-4 shrink-0 text-slate-400" />;
}

function RowTypeIcon({ type }: { type: string | null }) {
  const t = (type || '').toLowerCase();
  if (t === 'word' || t === 'scratch') {
    return <img src={WORD_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />;
  }
  if (t === 'excel' || t === 'xlsx' || t === 'xls' || t === 'csv') {
    return <img src={EXCEL_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />;
  }
  if (t === 'pdf') {
    return <img src={PDF_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />;
  }
  return (
    <div className="shrink-0 rounded-lg bg-slate-50 p-1.5 text-slate-400">
      <FileText className="h-[18px] w-[18px]" aria-hidden />
    </div>
  );
}

const ExecutionTemplatesPanel: React.FC<ExecutionTemplatesPanelProps> = ({
  executionId,
  onOpenProjectSelector,
}) => {
  const [rows, setRows] = useState<ExecutionTemplateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [catalog, setCatalog] = useState<CatalogTemplate[]>([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExecutionTemplateRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewRow, setPreviewRow] = useState<ExecutionTemplateRow | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<ExecutionTemplateRow | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!executionId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const list = await getExecutionTemplates(executionId);
      setRows(list);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, search]);

  const openLibrary = () => {
    setAddMenuOpen(false);
    setLibraryOpen(true);
    setCatalogSearch('');
  };

  useEffect(() => {
    if (!libraryOpen || !executionId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const list = await listTemplatesCatalog();
        if (!cancelled) setCatalog(list);
      } catch (e) {
        if (!cancelled) toast.error(e instanceof Error ? e.message : 'Failed to load catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [libraryOpen, executionId]);

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q)
    );
  }, [catalog, catalogSearch]);

  const handleAddFromLibrary = async (templateId: string) => {
    if (!executionId) return;
    setAdding(true);
    try {
      await addTemplateToExecution(executionId, templateId);
      toast.success('Template added');
      setLibraryOpen(false);
      await refresh();
      emitExecutionTemplatesChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add template');
    } finally {
      setAdding(false);
    }
  };

  const handleAddBlank = async (kind: 'word' | 'excel') => {
    if (!executionId) return;
    setAddMenuOpen(false);
    const templateId = kind === 'word' ? BLANK_TEMPLATE_ID : BLANK_EXCEL_TEMPLATE_ID;
    setAdding(true);
    try {
      await addTemplateToExecution(executionId, templateId);
      toast.success(kind === 'word' ? 'Blank Word template added' : 'Blank Excel template added');
      await refresh();
      emitExecutionTemplatesChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add template');
    } finally {
      setAdding(false);
    }
  };

  const triggerUploadDocument = () => {
    setAddMenuOpen(false);
    window.requestAnimationFrame(() => uploadInputRef.current?.click());
  };

  const handleUploadFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !executionId) return;
    setAdding(true);
    try {
      await addUploadedFileTemplateToExecution(executionId, file);
      toast.success('Template uploaded');
      await refresh();
      emitExecutionTemplatesChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not upload template');
    } finally {
      setAdding(false);
    }
  };

  const openRename = (row: ExecutionTemplateRow) => {
    setRenameDraft(row.name);
    setRenameTarget(row);
  };

  const confirmRename = async () => {
    if (!renameTarget) return;
    setRenaming(true);
    try {
      await updateExecutionTemplateFields(renameTarget.execution_template_id, { name: renameDraft });
      toast.success('Template renamed');
      setRenameTarget(null);
      await refresh();
      emitExecutionTemplatesChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not rename');
    } finally {
      setRenaming(false);
    }
  };

  const handleStatusChange = async (executionTemplateId: string, status: ExecutionTemplateStatus) => {
    setStatusUpdatingId(executionTemplateId);
    try {
      await updateExecutionTemplateFields(executionTemplateId, { status });
      toast.success('Estado actualizado');
      await refresh();
      emitExecutionTemplatesChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDownload = async (e: React.MouseEvent, row: ExecutionTemplateRow) => {
    e.stopPropagation();
    try {
      await downloadExecutionTemplateRow(row);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not download');
    }
  };

  const confirmDelete = async () => {
    if (!executionId || !deleteTarget) return;
    setDeleting(true);
    try {
      await deleteExecutionTemplate(executionId, deleteTarget.execution_template_id);
      toast.success('Template removed');
      if (configService.getConfig().activeExecutionTemplateId === deleteTarget.execution_template_id) {
        configService.setActiveExecutionTemplateId(null);
      }
      setDeleteTarget(null);
      await refresh();
      emitExecutionTemplatesChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not remove template');
    } finally {
      setDeleting(false);
    }
  };

  if (!executionId) {
    if (onOpenProjectSelector) {
      return (
        <TaskpanePanelShell variant="flex" className="items-center justify-center !p-4">
          <ChatBlockedState onOpenSelector={onOpenProjectSelector} />
        </TaskpanePanelShell>
      );
    }
    return (
      <TaskpanePanelShell>
        <PanelEmptyState
          icon={FileText}
          title="No execution selected"
          description="Select a project execution to manage templates."
          compact
        />
      </TaskpanePanelShell>
    );
  }

  const menuItemClass =
    'cursor-pointer gap-3 px-3 py-2 text-sm font-medium text-slate-600 data-[highlighted]:bg-slate-50 focus:bg-slate-50';

  const addTemplateDropdown = (
    <DropdownMenu open={addMenuOpen} onOpenChange={setAddMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 shrink-0 gap-2 border-slate-200 bg-white" disabled={adding}>
          <Plus className="h-3.5 w-3.5" />
          Add template
          <ChevronDown
            className={cn('h-3.5 w-3.5 opacity-70 transition-transform', addMenuOpen && 'rotate-180')}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
        <DropdownMenuItem onClick={openLibrary} className={menuItemClass}>
          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
          Add from library
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(ev) => {
            ev.preventDefault();
            triggerUploadDocument();
          }}
          className={menuItemClass}
        >
          <Upload className="h-4 w-4 shrink-0 text-slate-400" />
          Upload document
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleAddBlank('word')} className={menuItemClass}>
          <img src={WORD_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />
          Word
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleAddBlank('excel')} className={menuItemClass}>
          <img src={EXCEL_ICON_SRC} alt="" width={20} height={20} className="shrink-0" />
          Excel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <TaskpanePanelShell variant="flex">
      <input
        ref={uploadInputRef}
        type="file"
        accept=".docx,.xlsx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => void handleUploadFileChange(e)}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <PanelSectionHeader
          icon={FileText}
          title="Execution templates"
          count={!loading && rows.length > 0 ? rows.length : undefined}
          countLabel="templates"
          trailing={addTemplateDropdown}
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <PanelSearchField
            value={search}
            onChange={setSearch}
            placeholder="Search by name…"
            className="w-full sm:max-w-md"
          />
        </div>

        {loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : filteredRows.length === 0 ? (
            <PanelEmptyState
              icon={FileText}
              title={rows.length === 0 ? 'No execution templates yet' : 'No matches'}
              description={
                rows.length === 0
                  ? 'Use “Add template” to link templates to this project.'
                  : 'Try another search.'
              }
              compact
            />
          ) : (
            <div className="min-w-0 max-w-full overflow-x-auto rounded-2xl border border-white/60 bg-white/80 shadow-sm ring-1 ring-slate-900/5 backdrop-blur-sm">
              <table className="w-full table-fixed border-collapse text-left">
                <thead className="border-b border-slate-200/60 bg-slate-50/80 text-xs font-bold uppercase tracking-wider text-slate-500 backdrop-blur-sm">
                  <tr>
                    <th className="min-w-0 px-3 py-3">Template</th>
                    <th className="w-[100px] px-2 py-3">Estado</th>
                    <th className="w-[140px] px-1 py-3 text-right" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredRows.map((row) => (
                    <tr key={row.execution_template_id} className="transition-colors hover:bg-white">
                      <td className="min-w-0 px-3 py-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <RowTypeIcon type={row.type} />
                          <span className="truncate font-medium text-slate-800" title={row.name}>
                            {row.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <select
                          value={row.status}
                          disabled={statusUpdatingId === row.execution_template_id}
                          onChange={(e) =>
                            void handleStatusChange(
                              row.execution_template_id,
                              e.target.value as ExecutionTemplateStatus
                            )
                          }
                          className="h-8 max-w-[92px] rounded-md border border-slate-200 bg-white px-1.5 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-600 hover:text-indigo-600"
                            title="Rename"
                            onClick={() => openRename(row)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-600 hover:text-indigo-600"
                            title="Ver"
                            onClick={() => setPreviewRow(row)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-600 hover:text-indigo-600"
                            title="Descargar"
                            onClick={(e) => void handleDownload(e, row)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-600 hover:text-red-600"
                            title="Eliminar"
                            onClick={() => setDeleteTarget(row)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <ExecutionTemplatePreviewDialog
        open={!!previewRow}
        onOpenChange={(o) => {
          if (!o) setPreviewRow(null);
        }}
        row={previewRow}
      />

      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-3">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900">Add from library</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              placeholder="Search templates…"
              className="h-9 border-slate-200 pl-9 focus-visible:border-primary focus-visible:ring-primary/10"
            />
          </div>
          <div className="max-h-[50vh] min-h-[200px] divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/30">
            {catalogLoading ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : filteredCatalog.length === 0 ? (
              <p className="p-4 text-center text-sm text-slate-500">No templates found.</p>
            ) : (
              filteredCatalog.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={adding}
                  onClick={() => handleAddFromLibrary(t.id)}
                  className={cn(
                    'flex w-full gap-3 px-3 py-2.5 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-white disabled:opacity-50',
                    adding && 'pointer-events-none'
                  )}
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-slate-200/80">
                    <CatalogTypeIcon type={t.type} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-900">{t.name}</span>
                    <span className="mt-0.5 block text-xs font-normal capitalize text-slate-500">
                      {t.type || '—'}
                      {t.description
                        ? ` · ${t.description.slice(0, 72)}${t.description.length > 72 ? '…' : ''}`
                        : ''}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setLibraryOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(o) => {
          if (!o) setRenameTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename template</DialogTitle>
          </DialogHeader>
          <Input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            placeholder="Template name"
            className="border-slate-200 focus-visible:border-primary focus-visible:ring-primary/10"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void confirmRename();
              }
            }}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setRenameTarget(null)} disabled={renaming}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void confirmRename()} disabled={renaming}>
              {renaming ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove template?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This unlinks &quot;{deleteTarget?.name}&quot; from the execution and deletes its files in storage. This
            cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmDelete()} disabled={deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TaskpanePanelShell>
  );
};

export default ExecutionTemplatesPanel;
